import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { auth } from "@/authentication/Firebase.jsx";
import { getUnreadCount, subscribeUnreadChanged } from "@/services/NotificationCenterService.jsx";
import { useNotificationStream } from "@/pages/notifications/hooks/useNotificationStream.js";

/**
 * Fetches and tracks the current user's unread-notification count for the
 * selected workspace. Shared across any surface that wants to show the
 * badge (sidebar entry, header bell, etc.) without duplicating the fetch +
 * SSE subscription.
 *
 * @param {boolean} [enabled=true] — pass {@code false} to pause polling
 *        (e.g. on unauthenticated routes).
 * @returns {number} current unread count; 0 when loading / erroring.
 */
export function useUnreadNotificationCount(enabled = true) {
    const [count, setCount] = useState(0);
    // Mirror the SSE hook's gating: don't fire the GET until we actually have
    // a workspace context. The axios interceptor attaches X-Web-Org-Id from
    // this same value, so calling without it produces a 401 on the server.
    const orgId = useSelector((state) => state.dashboardContext?.selectedOrgId);

    const refresh = useCallback(async () => {
        try {
            const n = await getUnreadCount();
            // Math.max guards against the brief windows where the backend Redis
            // counter can drift negative under concurrent mark-read + dismiss races
            // on the same doc — the badge cannot show "-2" even if a stale build of
            // the service is deployed.
            const safe = typeof n === "number" ? Math.max(0, n) : 0;
            setCount(safe);
        } catch {
            // keep the stale badge visible — transient network errors
            // shouldn't make the badge flicker or disappear
        }
    }, []);

    // Initial fetch on mount and whenever auth/workspace context becomes
    // ready. Skipping while auth.currentUser is null avoids the early-boot
    // 401 that fires before Firebase has a token to hand to the interceptor.
    useEffect(() => {
        if (!enabled || !orgId || !auth.currentUser) return;
        refresh();
    }, [enabled, orgId, refresh]);

    // Realtime: every SSE notification re-pulls the authoritative count from the
    // backend rather than incrementing locally, so the badge can never drift out
    // of sync with reads/dismisses done elsewhere.
    useNotificationStream(
        useCallback(() => { refresh(); }, [refresh]),
        enabled
    );

    // Any read / dismiss / mark-all-read mutation through NotificationCenterService
    // fires `nc:unread-changed`; react by pulling the authoritative count.
    useEffect(() => {
        if (!enabled) return undefined;
        return subscribeUnreadChanged(refresh);
    }, [enabled, refresh]);

    // Self-heal on tab focus / visibility — recovers from reads done in another
    // tab/browser that this tab's `nc:unread-changed` never saw.
    useEffect(() => {
        if (!enabled || typeof document === "undefined") return undefined;
        const onReturn = () => refresh();
        const onVisible = () => { if (!document.hidden) onReturn(); };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onReturn);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onReturn);
        };
    }, [enabled, refresh]);

    return count;
}
